/**
 * Personnel — who works here.
 *
 * ---------------------------------------------------------------------
 * THE LIST OPENS ON WHO IS HERE NOW
 *
 * A club that has run fifteen summers has several hundred former pool
 * attendants and four current ones. A list that opens on all of them is
 * a list nobody uses, so former employees are behind a toggle — and the
 * toggle says how many there are, because "show former staff" with
 * nothing behind it is a button that lies.
 *
 * ---------------------------------------------------------------------
 * PAY IS A DELIBERATE ACT, AND IT SAYS SO
 *
 * The figure is not in the list response. It cannot be: the application
 * role has no SELECT privilege on the column, and the one path is a
 * database function that writes an audit row before it returns.
 *
 * So the screen does not show pay and offer to hide it — it shows a
 * button, and next to the button it says that pressing it is recorded.
 * That sentence is the point. A control the reader does not know about
 * deters nobody, and this control's whole job is deterrence: everybody
 * in this table knows everybody else in it.
 *
 * ---------------------------------------------------------------------
 * A RATE IS ADDED, NEVER EDITED
 *
 * The form asks for a date the new figure takes effect, not for a
 * correction to the old one. STAFF-6 refuses to change a rate whose date
 * has arrived, for the same reason the ledger is corrected by reversal
 * rather than by editing: pay history is what a wage dispute is settled
 * from, and a record that can be rewritten settles nothing.
 */

import { useState } from 'react';
import { api } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { useSort, SortTh, SortPicker } from '../lib/useSort.tsx';
import { useSession } from '../lib/session.tsx';
import { date as fmtDate, dateTime, money, parseMoney, titleCase } from '../lib/format.ts';
import { Card, Empty, ErrorBanner, Loading, Status, Tile } from '../components/ui.tsx';

interface StaffRow {
  id: string;
  person_id: string;
  display_name: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  department_id: string;
  department: string;
  job_title: string;
  employment_type: string;
  is_exempt: boolean;
  employee_number: string | null;
  hired_on: string;
  ended_on: string | null;
  ended_reason: string | null;
  is_current: boolean;
  reports_to_id: string | null;
  reports_to_title: string | null;
  years_of_service: number;
  app_user_id: string | null;
  app_role: string | null;
  pay_basis: string | null;
  pay_effective_on: string | null;
}

interface Headcount {
  department_id: string;
  department: string;
  current_headcount: number;
  full_time: number;
  part_time: number;
  seasonal: number;
  contract: number;
  left_in_last_year: number;
}

interface StaffResponse {
  staff: StaffRow[];
  departments: Headcount[];
  employment_types: string[];
  end_reasons: string[];
  pay_bases: string[];
  may_see_pay: boolean;
}

export function StaffPage() {
  const { atLeast } = useSession();
  const [department, setDepartment] = useState('');
  const [q, setQ] = useState('');
  const [former, setFormer] = useState(false);

  const params = new URLSearchParams();
  if (department) params.set('department', department);
  if (q.trim()) params.set('q', q.trim());
  if (former) params.set('include_former', 'true');

  const { data, error, loading, reload } = useApi<StaffResponse>(
    `/v1/staff${params.toString() ? `?${params}` : ''}`,
    [department, q, former],
  );

  // `loading` alone would unmount the whole page on every keystroke in
  // the search box. Only the first load is a blank page.
  if (loading && !data) return <Loading />;

  const staff = data?.staff ?? [];

  /* A department head reads this by department, a controller by hire
     date, and whoever is chasing a leaver reads it by status. */
  const sorted = useSort(
    staff,
    {
      name: (s) => s.last_name || s.display_name,
      job_title: (s) => s.job_title,
      department: (s) => s.department,
      type: (s) => s.employment_type,
      since: (s) => s.hired_on,
      status: (s) => (s.is_current ? 'current' : 'ended'),
    },
    { key: 'name' },
  );
  const departments = (data?.departments ?? []).filter(
    (d) => d.current_headcount > 0 || d.left_in_last_year > 0,
  );

  const totalCurrent = departments.reduce((n, d) => n + d.current_headcount, 0);
  const leftThisYear = departments.reduce((n, d) => n + d.left_in_last_year, 0);
  const seasonal = departments.reduce((n, d) => n + d.seasonal, 0);

  return (
    <>
      <h1>Personnel</h1>
      <ErrorBanner error={error} />

      <div className="tiles">
        <Tile label="On the payroll" value={String(totalCurrent)} />
        <Tile label="Seasonal" value={String(seasonal)} note="of the current headcount" />
        <Tile label="Departments staffed" value={String(departments.length)} />
        <Tile label="Left in the last year" value={String(leftThisYear)} />
      </div>

      <Card
        title="Everyone"
        actions={
          <div className="row" style={{ gap: 8 }}>
            <input
              placeholder="Name or job title"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <select value={department} onChange={(e) => setDepartment(e.target.value)}>
              <option value="">Every department</option>
              {departments.map((d) => (
                <option key={d.department_id} value={d.department_id}>
                  {d.department} ({d.current_headcount})
                </option>
              ))}
            </select>
            <button className="secondary small" onClick={() => setFormer((f) => !f)}>
              {former ? 'Hide former staff' : `Show former staff (${leftThisYear}+)`}
            </button>
          </div>
        }
        flush
      >
        {staff.length === 0 ? (
          <Empty>
            {q || department
              ? 'Nobody here matches that.'
              : 'Nobody is recorded as working here yet. Until somebody is, "staff" means a login role and nothing more — no start dates, no departments, and no labor cost.'}
          </Empty>
        ) : (
          <div className="table">
            {/* Same `data--cards` treatment as the member roster: on a
                phone each row becomes a block rather than scrolling
                sideways, and `data-label` is what each cell calls
                itself once the header row is gone. */}
            {/* Which also clips the header row away, so the sort needs a
                control of its own down there. */}
            <SortPicker
              s={sorted}
              labels={{
                name: 'Name',
                job_title: 'Job title',
                department: 'Department',
                type: 'Employment type',
                since: 'Start date',
                status: 'Status',
              }}
            />
            <table className="data data--cards">
              <thead>
                <tr>
                  <SortTh s={sorted} col="name">Name</SortTh>
                  <SortTh s={sorted} col="job_title">Job title</SortTh>
                  <SortTh s={sorted} col="department">Department</SortTh>
                  <SortTh s={sorted} col="type">Type</SortTh>
                  <SortTh s={sorted} col="since">Since</SortTh>
                  <SortTh s={sorted} col="status">Status</SortTh>
                  {/* Pay is not offered as a sort. Ranking colleagues by
                      what they earn is a thing to build deliberately, on
                      a screen that says who may see it, not a side effect
                      of a clickable heading. */}
                  {data?.may_see_pay && <th>Pay</th>}
                </tr>
              </thead>
              <tbody>
                {sorted.rows.map((s) => (
                  <StaffLine
                    key={s.id}
                    row={s}
                    maySeePay={data?.may_see_pay ?? false}
                    onChanged={reload}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {atLeast('club_admin') && (
        <Hire departments={data?.departments ?? []} staff={staff} onDone={reload} />
      )}

      {atLeast('club_admin') && <PayAccess />}
    </>
  );
}

function StaffLine({
  row,
  maySeePay,
  onChanged,
}: {
  row: StaffRow;
  maySeePay: boolean;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <tr>
        <td data-label="Name" className="cell--title">
          {row.display_name}
          {row.app_role && (
            <span className="muted small cell--note"> · signs in as {row.app_role}</span>
          )}
        </td>
        <td data-label="Job title">
          {row.job_title}
          {row.reports_to_title && (
            <span className="muted small cell--note"> · reports to {row.reports_to_title}</span>
          )}
        </td>
        <td className="muted cell--aside" data-label="Department">
          {row.department}
        </td>
        <td className="muted cell--aside" data-label="Type">
          {titleCase(row.employment_type.replace('_', ' '))}
          {row.is_exempt && <span className="muted small cell--note"> · exempt</span>}
        </td>
        <td data-label="Since">
          {fmtDate(row.hired_on)}
          <span className="muted small cell--note">
            {' '}
            {row.years_of_service < 1
              ? 'under a year'
              : `${row.years_of_service} year${row.years_of_service === 1 ? '' : 's'}`}
          </span>
        </td>
        <td data-label="Status">
          {row.is_current ? (
            <Status tone="good">Current</Status>
          ) : (
            <Status tone="neutral">
              {titleCase((row.ended_reason ?? 'left').replace('_', ' '))} {fmtDate(row.ended_on)}
            </Status>
          )}
        </td>
        {maySeePay && (
          <td data-label="Pay">
            {row.pay_basis ? (
              <button className="secondary small" onClick={() => setOpen((o) => !o)}>
                {open ? 'Hide' : `Show ${row.pay_basis}`}
              </button>
            ) : (
              <span className="muted small">no rate on file</span>
            )}
          </td>
        )}
      </tr>
      {open && (
        <tr>
          <td colSpan={maySeePay ? 7 : 6}>
            <Pay staff={row} onChanged={onChanged} />
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * The pay history for one person, fetched only when asked for.
 *
 * Deliberately not prefetched and deliberately not fetched on hover:
 * every call writes an audit row naming the reader, and a log full of
 * reads nobody meant to make is a log nobody can use.
 */
function Pay({ staff, onChanged }: { staff: StaffRow; onChanged: () => void }) {
  const { currency } = useSession();
  const { data, error, loading, reload } = useApi<{ rates: any[] }>(`/v1/staff/${staff.id}/pay`);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    effective_on: new Date().toISOString().slice(0, 10),
    basis: staff.pay_basis ?? 'hourly',
    amount: '',
    reason: 'merit',
  });

  const amountMinor = parseMoney(form.amount, currency);

  const add = useMutation(async () =>
    api(`/v1/staff/${staff.id}/pay`, {
      method: 'POST',
      body: {
        effective_on: form.effective_on,
        basis: form.basis,
        amount_minor: amountMinor,
        reason: form.reason,
      },
    }),
  );

  if (loading && !data) return <Loading rows={1} />;

  const rates = data?.rates ?? [];
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div>
      <ErrorBanner error={error ?? add.error} />
      <p className="muted small">
        This read is recorded — who opened it, when, and against whom. The figures themselves are
        never written to the log.
      </p>

      {rates.length === 0 ? (
        <Empty>No rate has ever been recorded for this employment.</Empty>
      ) : (
        <div className="table">
          <table className="data">
            <thead>
              <tr>
                <th>From</th>
                <th>Basis</th>
                <th className="num">Amount</th>
                <th>Why</th>
              </tr>
            </thead>
            <tbody>
              {rates.map((r: any) => (
                <tr key={r.effective_on}>
                  <td>
                    {fmtDate(r.effective_on)}
                    {r.effective_on > today && (
                      <span className="muted small"> · not yet in effect</span>
                    )}
                  </td>
                  <td className="muted">{r.basis === 'salary' ? 'Salary, per year' : 'Per hour'}</td>
                  <td className="num">{money(r.amount_minor, currency)}</td>
                  <td className="muted">{titleCase((r.reason ?? '—').replace('_', ' '))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!adding ? (
        <div className="actions">
          <button className="secondary small" onClick={() => setAdding(true)}>
            Record a new rate
          </button>
        </div>
      ) : (
        <>
          <div className="grid-2">
            <label className="field">
              <span>From</span>
              <input
                type="date"
                value={form.effective_on}
                onChange={(e) => setForm((f) => ({ ...f, effective_on: e.target.value }))}
              />
              <div className="hint">
                The date the new figure takes effect. Rates already in effect cannot be changed —
                record a new one instead.
              </div>
            </label>
            <label className="field">
              <span>Basis</span>
              <select
                value={form.basis}
                onChange={(e) => setForm((f) => ({ ...f, basis: e.target.value }))}
              >
                <option value="hourly">Per hour</option>
                <option value="salary">Salary, per year</option>
              </select>
            </label>
            <label className="field">
              <span>Amount</span>
              <input
                value={form.amount}
                onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                placeholder={form.basis === 'salary' ? '68,000.00' : '22.50'}
              />
              {form.amount !== '' && amountMinor === null && (
                <span className="muted small">That is not an amount this currency can hold.</span>
              )}
            </label>
            <label className="field">
              <span>Why</span>
              <select
                value={form.reason}
                onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
              >
                <option value="merit">Merit increase</option>
                <option value="promotion">Promotion</option>
                <option value="market">Market adjustment</option>
                <option value="minimum_wage">Minimum wage</option>
                <option value="correction">Correction</option>
                <option value="other">Other</option>
              </select>
            </label>
          </div>
          <div className="actions">
            <button
              disabled={add.busy || amountMinor === null || amountMinor <= 0}
              onClick={async () => {
                const r = await add.run();
                if (r) {
                  setAdding(false);
                  setForm((f) => ({ ...f, amount: '' }));
                  reload();
                  onChanged();
                }
              }}
            >
              {add.busy ? 'Recording…' : 'Record'}
            </button>
            <button className="secondary" onClick={() => setAdding(false)}>
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Hiring, and ending an employment.
 *
 * The two live together because they are the same decision made twice,
 * and because a screen that can only add is how a club ends up with a
 * payroll full of people who left.
 */
function Hire({
  departments,
  staff,
  onDone,
}: {
  departments: Headcount[];
  staff: StaffRow[];
  onDone: () => void;
}) {
  const { currency, role } = useSession();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    department_id: '',
    job_title: '',
    employment_type: 'full_time',
    is_exempt: false,
    hired_on: new Date().toISOString().slice(0, 10),
    reports_to_id: '',
    pay_basis: 'hourly',
    amount: '',
  });

  const mayRecordPay = role === 'controller' || role === 'club_admin';
  const amountMinor = form.amount.trim() === '' ? null : parseMoney(form.amount, currency);

  const create = useMutation(async () =>
    api('/v1/staff', {
      method: 'POST',
      body: {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim() || undefined,
        department_id: form.department_id,
        job_title: form.job_title.trim(),
        employment_type: form.employment_type,
        is_exempt: form.is_exempt,
        hired_on: form.hired_on,
        reports_to_id: form.reports_to_id || undefined,
        pay_basis: amountMinor === null ? undefined : form.pay_basis,
        pay_amount_minor: amountMinor ?? undefined,
      },
    }),
  );

  const ready =
    form.first_name.trim() !== '' &&
    form.last_name.trim() !== '' &&
    form.job_title.trim() !== '' &&
    form.department_id !== '' &&
    (form.amount.trim() === '' || (amountMinor !== null && amountMinor > 0));

  return (
    <Card
      title="Hiring and Leaving"
      actions={
        <button className="secondary small" onClick={() => setOpen((o) => !o)}>
          {open ? 'Close' : 'Hire somebody'}
        </button>
      }
    >
      {open && (
        <>
          <ErrorBanner error={create.error} />
          <div className="grid-2">
            <label className="field">
              <span>First name</span>
              <input
                value={form.first_name}
                onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
              />
            </label>
            <label className="field">
              <span>Last name</span>
              <input
                value={form.last_name}
                onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
              />
            </label>
            <label className="field">
              <span>Email</span>
              <input
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="optional"
              />
            </label>
            <label className="field">
              <span>Department</span>
              <select
                value={form.department_id}
                onChange={(e) => setForm((f) => ({ ...f, department_id: e.target.value }))}
              >
                <option value="">Choose one</option>
                {departments.map((d) => (
                  <option key={d.department_id} value={d.department_id}>
                    {d.department}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Job title</span>
              <input
                value={form.job_title}
                onChange={(e) => setForm((f) => ({ ...f, job_title: e.target.value }))}
                placeholder="Sous Chef"
              />
            </label>
            <label className="field">
              <span>Employment</span>
              <select
                value={form.employment_type}
                onChange={(e) => setForm((f) => ({ ...f, employment_type: e.target.value }))}
              >
                <option value="full_time">Full time</option>
                <option value="part_time">Part time</option>
                <option value="seasonal">Seasonal</option>
                <option value="contract">Contract</option>
              </select>
            </label>
            <label className="field">
              <span>Started</span>
              <input
                type="date"
                value={form.hired_on}
                onChange={(e) => setForm((f) => ({ ...f, hired_on: e.target.value }))}
              />
            </label>
            <label className="field">
              <span>Reports to</span>
              <select
                value={form.reports_to_id}
                onChange={(e) => setForm((f) => ({ ...f, reports_to_id: e.target.value }))}
              >
                <option value="">Nobody in this list</option>
                {staff
                  .filter((s) => s.is_current)
                  .map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.display_name} — {s.job_title}
                    </option>
                  ))}
              </select>
            </label>
            {mayRecordPay && (
              <>
                <label className="field">
                  <span>Opening rate</span>
                  <input
                    value={form.amount}
                    onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                    placeholder="optional"
                  />
                  <div className="hint">
                    Leave it empty if nobody remembers it — the employment is still worth recording
                    without it.
                  </div>
                </label>
                <label className="field">
                  <span>Basis</span>
                  <select
                    value={form.pay_basis}
                    onChange={(e) => setForm((f) => ({ ...f, pay_basis: e.target.value }))}
                  >
                    <option value="hourly">Per hour</option>
                    <option value="salary">Salary, per year</option>
                  </select>
                </label>
              </>
            )}
          </div>
          <label className="row" style={{ gap: 8, marginBottom: 12 }}>
            <input
              type="checkbox"
              checked={form.is_exempt}
              onChange={(e) => setForm((f) => ({ ...f, is_exempt: e.target.checked }))}
            />
            <span className="muted small">
              Exempt from overtime. This is a legal determination about duties and salary level —
              record what the club's payroll processor decided, not what looks right.
            </span>
          </label>
          <div className="actions">
            <button
              disabled={create.busy || !ready}
              onClick={async () => {
                const r = await create.run();
                if (r) {
                  setOpen(false);
                  setForm((f) => ({
                    ...f,
                    first_name: '',
                    last_name: '',
                    email: '',
                    job_title: '',
                    amount: '',
                  }));
                  onDone();
                }
              }}
            >
              {create.busy ? 'Hiring…' : 'Hire'}
            </button>
            <button className="secondary" onClick={() => setOpen(false)}>
              Cancel
            </button>
          </div>
        </>
      )}

      <EndEmployment staff={staff.filter((s) => s.is_current)} onDone={onDone} />
    </Card>
  );
}

function EndEmployment({ staff, onDone }: { staff: StaffRow[]; onDone: () => void }) {
  const [id, setId] = useState('');
  const [on, setOn] = useState(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState('resigned');

  const end = useMutation(async () =>
    api(`/v1/staff/${id}/end`, { method: 'POST', body: { ended_on: on, reason } }),
  );

  return (
    <>
      <ErrorBanner error={end.error} />
      <div className="grid-2">
        <label className="field">
          <span>Somebody is leaving</span>
          <select value={id} onChange={(e) => setId(e.target.value)}>
            <option value="">Choose one</option>
            {staff.map((s) => (
              <option key={s.id} value={s.id}>
                {s.display_name} — {s.job_title}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Last day</span>
          <input type="date" value={on} onChange={(e) => setOn(e.target.value)} />
        </label>
        <label className="field">
          <span>Why</span>
          <select value={reason} onChange={(e) => setReason(e.target.value)}>
            <option value="resigned">Resigned</option>
            <option value="dismissed">Dismissed</option>
            <option value="season_ended">Season ended</option>
            <option value="retired">Retired</option>
            <option value="other">Other</option>
          </select>
        </label>
      </div>
      <div className="actions">
        <button
          className="secondary"
          disabled={end.busy || !id}
          onClick={async () => {
            const r = await end.run();
            if (r) {
              setId('');
              onDone();
            }
          }}
        >
          {end.busy ? 'Recording…' : 'Record the leaving'}
        </button>
      </div>
      <p className="muted small" style={{ marginBottom: 0 }}>
        The record stays. Anybody who reported to them is moved up a level so the org chart does not
        stop at a person who has gone, and a seasonal employee who comes back next May is hired
        again rather than edited back to life.
      </p>
    </>
  );
}

/**
 * Who has read pay, and what for.
 *
 * The half of the control that does anything. A door that logs and whose
 * log nobody opens is a door that does not log.
 */
function PayAccess() {
  const { data, error, loading } = useApi<{ reads: any[] }>('/v1/staff/pay-access');
  if (loading && !data) return null;

  const reads = data?.reads ?? [];

  return (
    <Card title="Who Has Looked at Pay">
      <ErrorBanner error={error} />
      {reads.length === 0 ? (
        <Empty>Nobody has opened a pay figure in the last ninety days.</Empty>
      ) : (
        <div className="table">
          <table className="data">
            <thead>
              <tr>
                <th>When</th>
                <th>Who</th>
                <th>Whose</th>
                <th>Why they said</th>
              </tr>
            </thead>
            <tbody>
              {reads.map((r: any, i: number) => (
                <tr key={i}>
                  <td className="muted">{dateTime(r.occurred_at)}</td>
                  <td>
                    {r.actor_name ?? 'an unnamed login'}
                    <span className="muted small"> · {r.actor_role}</span>
                  </td>
                  <td>{r.job_title ?? '—'}</td>
                  <td className="muted">{r.purpose}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
